import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  FaFacebookF,
  FaYoutube,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhoneAlt,
} from "react-icons/fa";

import { HiOutlineMenuAlt3 } from "react-icons/hi";
import { IoClose } from "react-icons/io5";

import logo from "./zpa.png";
import "./NavbarRoyalPriest.css";

const navLinks = [
  {
    text: "Home",
    path: "/",
  },
  {
    text: "Principal Message",
    path: "/principalMessagePage",
  },
  {
    text: "Campus",
    path: "/campusPage",
  },
  {
    text: "Scan Attendance",
    path: "/teacherAttendanceScanner",
  },
  {
    text: "Login",
    path: "/login",
    button: true,
  },
];

const Navbar = () => {
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const mobileMenuRef = useRef(null);
  const mobileToggleRef = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add("navbar-menu-open");
    } else {
      document.body.classList.remove("navbar-menu-open");
    }

    return () => {
      document.body.classList.remove("navbar-menu-open");
    };
  }, [menuOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && menuOpen) {
        setMenuOpen(false);
        mobileToggleRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!menuOpen) return;

      const clickedInsideMenu =
        mobileMenuRef.current?.contains(event.target);

      const clickedToggle =
        mobileToggleRef.current?.contains(event.target);

      if (!clickedInsideMenu && !clickedToggle) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [menuOpen]);

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    return location.pathname === path;
  };

  const toggleMenu = () => {
    setMenuOpen((previous) => !previous);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  return (
    <>
      <header
        className={`site-header ${
          scrolled ? "site-header-scrolled" : ""
        }`}
      >
        <div className="top-navbar">
          <div className="top-navbar-inner">
            <div className="top-navbar-left">
              <div className="top-item location-item">
                <FaMapMarkerAlt />

                <span>
                  Cowfield 1st Junction Paynesville,
                  Monrovia, Liberia
                </span>
              </div>

              <div className="top-item phone-item">
                <FaPhoneAlt />

                {/* <span>+231 776 494 010</span> */}
              </div>
            </div>

            <div className="top-navbar-right">
              <span className="follow-text">
                Follow Us
              </span>

              <div className="top-socials">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Zion Praise Academy on Facebook"
                >
                  <FaFacebookF />
                </a>

                <a
                  href="https://youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Zion Praise Academy on YouTube"
                >
                  <FaYoutube />
                </a>

                <a
                  href="mailto:info@messiahmission.edu"
                  aria-label="Email Zion Praise Academy"
                >
                  <FaEnvelope />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="navbar-main">
          <div className="navbar-main-inner">
            <Link
              to="/"
              className="logo-section"
              aria-label="Zion Praise Academy Home"
            >
              <div className="logo-wrapper">
                <img
                  src={logo}
                  alt="Zion Praise Academy"
                  className="navbar-logo"
                />
              </div>

              <div className="school-name">
                <h1>Zion Praise Academy</h1>

                <p>CRM School System</p>
              </div>
            </Link>

            <nav
              className="desktop-navigation"
              aria-label="Main navigation"
            >
              <ul className="nav-links">
                {navLinks.map((link) => (
                  <li
                    key={link.path}
                    className={
                      isActive(link.path) ? "active" : ""
                    }
                  >
                    <Link
                      to={link.path}
                      className={
                        link.button ? "login-button" : ""
                      }
                    >
                      <span>{link.text}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <button
              ref={mobileToggleRef}
              type="button"
              className={`mobile-toggle ${
                menuOpen ? "toggle-active" : ""
              }`}
              onClick={toggleMenu}
              aria-label={
                menuOpen
                  ? "Close navigation menu"
                  : "Open navigation menu"
              }
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
            >
              {menuOpen ? (
                <IoClose />
              ) : (
                <HiOutlineMenuAlt3 />
              )}
            </button>
          </div>
        </div>

        <div
          className={`mobile-backdrop ${
            menuOpen ? "show" : ""
          }`}
          onClick={closeMenu}
          aria-hidden="true"
        />

        <aside
          ref={mobileMenuRef}
          id="mobile-navigation"
          className={`mobile-menu ${
            menuOpen ? "show" : ""
          }`}
          aria-hidden={!menuOpen}
        >
          <div className="mobile-menu-header">
            <div className="mobile-brand">
              <img
                src={logo}
                alt=""
              />

              <div>
                <strong>Zion Praise</strong>

                <span>Academy</span>
              </div>
            </div>

            <button
              type="button"
              className="mobile-close-button"
              onClick={closeMenu}
              aria-label="Close navigation menu"
            >
              <IoClose />
            </button>
          </div>

          <div className="mobile-menu-divider" />

          <nav
            className="mobile-navigation"
            aria-label="Mobile navigation"
          >
            <ul>
              {navLinks.map((link) => (
                <li
                  key={link.path}
                  className={
                    isActive(link.path) ? "active" : ""
                  }
                >
                  <Link
                    to={link.path}
                    onClick={closeMenu}
                    tabIndex={menuOpen ? 0 : -1}
                    className={
                      link.button
                        ? "mobile-login-button"
                        : ""
                    }
                  >
                    <span>{link.text}</span>

                    {!link.button && (
                      <span className="mobile-arrow">
                        →
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="mobile-contact">
            <div className="mobile-contact-item">
              <FaMapMarkerAlt />

              <span>
                New Jerusalem Community,
                Johnsonville, Monrovia, Liberia
              </span>
            </div>

            <div className="mobile-contact-item">
              <FaPhoneAlt />

              {/* <span>+231 776 494 010</span> */}
            </div>
          </div>

          <div className="mobile-menu-divider" />

          <div className="mobile-social-section">
            <span>Connect With Us</span>

            <div className="mobile-socials">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FaFacebookF />
              </a>

              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <FaYoutube />
              </a>

              <a
                href="mailto:info@messiahmission.edu"
                aria-label="Email"
              >
                <FaEnvelope />
              </a>
            </div>
          </div>

          <div className="mobile-menu-footer">
            <span>Zion Praise Academy</span>

            <small>
              Equipping for a Better Future
            </small>
          </div>
        </aside>
      </header>

      <div className="navbar-spacing" />
    </>
  );
};

export default Navbar;
